// Loads Roboto Regular/Bold from a CDN once and caches the base64 payloads so
// jsPDF can render Romanian diacritics (ă, ș, ț, etc.) which are not part of
// the WinAnsi-1252 encoding used by jsPDF's bundled Helvetica.
//
// Source: https://github.com/googlefonts/roboto (Apache 2.0)
const FONT_URLS = {
  regular: 'https://cdn.jsdelivr.net/gh/googlefonts/roboto@main/src/hinted/Roboto-Regular.ttf',
  bold: 'https://cdn.jsdelivr.net/gh/googlefonts/roboto@main/src/hinted/Roboto-Bold.ttf'
};

export type RobotoFontPayloads = {
  regular: string;
  bold: string;
};

let cachedFonts: RobotoFontPayloads | null = null;
let inflight: Promise<RobotoFontPayloads> | null = null;

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

const fetchAsBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch font ${url}: HTTP ${response.status}`);
  }
  return arrayBufferToBase64(await response.arrayBuffer());
};

export const loadRobotoFonts = async (): Promise<RobotoFontPayloads> => {
  if (cachedFonts) return cachedFonts;
  if (inflight) return inflight;

  inflight = (async () => {
    const [regular, bold] = await Promise.all([
      fetchAsBase64(FONT_URLS.regular),
      fetchAsBase64(FONT_URLS.bold)
    ]);
    cachedFonts = { regular, bold };
    return cachedFonts;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
};
